import React, { useState, useEffect } from 'react';
import { TextField, Typography, Box, Button, Grid, Container } from '@mui/material';
import Api, { APIs } from "../../../../Api";
import { dialogBoxRef } from "../../../base/components/ContentRouter";

export interface IProjectSummary {
    id: string;
    project_id: string;
    title: string;
    component_name: string;
    approving_authority: string;
    financial_progress: number;
    exec_agency: string;
    physical_progress: number;
    region: string;
    sector: string;
    dev_partner: string;
    donor_funding: number;
    adp_share: number;
    approval_date: string; // ISO date format "YYYY-MM-DD"
    Financial_Spending: number;
    total_cost: number;
}

interface ICumulativeSpending {
    id: string;
    date: string; // ISO date format "YYYY-MM-DD"
    cumulative_spending: number;
}

interface IPhysicalProgress {
    id: string;
    date: string; // ISO date format "YYYY-MM-DD"
    physical_progress: number;
}

interface IProps {
    data: IProjectSummary;
}

const ProjectSummaryEditor = (props: IProps) => {
    const [projectData, setProjectData] = useState(props.data);
    const [cumulativeSpending, setCumulativeSpending] = useState<ICumulativeSpending[]>([]);
    const [physicalProgress, setPhysicalProgress] = useState<IPhysicalProgress[]>([]);
    const [lastId, setLastId] = useState(-1);
    const [componentName, setComponentName] = useState(props.data?.component_name); // Editable component name
    const [execAgency, setExecAgency] = useState(props.data?.exec_agency); // Editable executing agency
    const manage_info_id = 9;

    const getTodayDate = (): string => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    const handleComponentNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setComponentName(event.target.value);
    };

    const handleExecAgencyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setExecAgency(event.target.value);
    };

    const handleSaveChanges = () => {
        if (!componentName.trim() || !execAgency.trim()) {
            alert("Component Name and Executing Agency cannot be empty.");
            return;
        }

        const data = {
            component_name: componentName,
            exec_agency: execAgency, // Include the exec_agency in the payload
            financial: cumulativeSpending,
            physical: physicalProgress,
        };

        Api.post(APIs.MoPD_UPDATE_PROJECT_PROGRESS, data, { pid: projectData.id, manage_info_id: manage_info_id })
            .then(() => {
                dialogBoxRef?.current?.closeDialog();
                alert("Save successful");

                setTimeout(() => window.location.reload(), 500);
            })
            .catch(error => {
                console.error("Error saving changes:", error);
            });
    };

    useEffect(() => {
        if (projectData) {
            Api.get(APIs.MoPD_PROJECT_FINANCIAL, { pid: projectData.id })
                .then((payload: { acum_spending: ICumulativeSpending[] }) => {
                    setCumulativeSpending(payload.acum_spending.slice(0, 1));
                })
                .catch((error) => {
                    console.error("Error fetching financial data:", error);
                });

            Api.get(APIs.MoPD_PROJECT_PHYSICAL, { pid: projectData.id })
                .then((payload: { acum_physical: IPhysicalProgress[] }) => {
                    setPhysicalProgress(payload.acum_physical.slice(0, 1));
                })
                .catch((error) => {
                    console.error("Error fetching physical data:", error);
                });
        }
    }, [projectData]);

    return (
        <Container>
            <Typography variant="h4" gutterBottom>
                {projectData.title}
            </Typography>

            <Box sx={{ border: '1px solid black', padding: 2, borderRadius: 2, marginBottom: 3 }}>

                <Typography variant="body1" color={"blue"}>
                    <strong>Project ID:</strong> {projectData.project_id} - {projectData.id}
                </Typography>
                <Typography variant="body1" color={"blue"}>
                    <strong>Development Partner:</strong> {projectData.dev_partner}
                </Typography>
                <Typography variant="body1" color={"blue"}>
                    <strong>Region:</strong> {projectData.region}
                </Typography>
                <Typography variant="body1" color={"blue"}>
                    <strong>Sector:</strong> {projectData.sector}
                </Typography>
                <Typography variant="body1" color={"blue"}>
                    <strong>Physical Progress:</strong> {projectData.physical_progress}%
                </Typography>
                <Typography variant="body1" color={"blue"}>
                    <strong>Financial Spending:</strong> {projectData.Financial_Spending}
                </Typography>

                {/* Editable Component Name */}
                <Typography variant="h6" gutterBottom>
                    Component Name
                </Typography>
                <TextField
                    label="Component Name"
                    value={componentName}
                    onChange={handleComponentNameChange}
                    fullWidth
                />

                {/* Editable Executing Agency */}
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Executing Agency
                </Typography>
                <TextField
                    label="Executing Agency"
                    value={execAgency}
                    onChange={handleExecAgencyChange}
                    fullWidth
                />

                {/* Editable Cumulative Spending */}
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Cumulative Financial Spending
                </Typography>
                {cumulativeSpending.map((spending, index) => (
                    <Grid container spacing={2} key={spending.id}>
                        <Grid item xs={5}>
                            <TextField
                                label="Date"
                                type="date"
                                value={spending.date}
                                onChange={(e) => setCumulativeSpending(prev => prev.map(item => item.id === spending.id ? { ...item, date: e.target.value } : item))}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={5}>
                            <TextField
                                label="Cumulative Spending"
                                type="number"
                                value={spending.cumulative_spending}
                                onChange={(e) => setCumulativeSpending(prev => prev.map(item => item.id === spending.id ? { ...item, cumulative_spending: Number(e.target.value) } : item))}
                                fullWidth
                            />
                        </Grid>
                        {index > 0 && (
                            <Grid item xs={2}>
                                <Button variant="contained" color="secondary" onClick={() => setCumulativeSpending(prev => prev.filter(item => item.id !== spending.id))}>
                                    Delete
                                </Button>
                            </Grid>
                        )}
                    </Grid>
                ))}

                <Button variant="contained" onClick={() => setCumulativeSpending(prev => [...prev, { id: lastId.toString(), date: getTodayDate(), cumulative_spending: 0 }])} sx={{ mt: 2 }}>
                    Add New Cumulative Spending Row
                </Button>

                {/* Editable Physical Progress */}
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Physical Progress
                </Typography>
                {physicalProgress.map((physical, index) => (
                    <Grid container spacing={2} key={physical.id}>
                        <Grid item xs={5}>
                            <TextField
                                label="Date"
                                type="date"
                                value={physical.date}
                                onChange={(e) => setPhysicalProgress(prev => prev.map(item => item.id === physical.id ? { ...item, date: e.target.value } : item))}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={5}>
                            <TextField
                                label="Physical Progress"
                                type="number"
                                value={physical.physical_progress}
                                onChange={(e) => setPhysicalProgress(prev => prev.map(item => item.id === physical.id ? { ...item, physical_progress: Number(e.target.value) } : item))}
                                fullWidth
                            />
                        </Grid>
                        {index > 0 && (
                            <Grid item xs={2}>
                                <Button variant="contained" color="secondary" onClick={() => setPhysicalProgress(prev => prev.filter(item => item.id !== physical.id))}>
                                    Delete
                                </Button>
                            </Grid>
                        )}
                    </Grid>
                ))}

                <Button variant="contained" onClick={() => setPhysicalProgress(prev => [...prev, { id: lastId.toString(), date: getTodayDate(), physical_progress: 0 }])} sx={{ mt: 2 }}>
                    Add New Physical Progress Row
                </Button>

                {/* Save Changes Button */}
                <Button variant="contained" color="primary" onClick={handleSaveChanges} sx={{ mt: 3 }} fullWidth>
                    Save Changes
                </Button>
            </Box>
        </Container>
    );
};

export default ProjectSummaryEditor;
